<template>
  <div v-if="programmeModule" class="panel module-card-container" id="dashboard-module">
    <div @click="onHeaderClick" class="panel-body clickable">
      <div>
        <div class="module-name">
          <h3>{{ programmeModule.name }}</h3>
        </div>
        <div class="module-progress">
          <k-progress
            shape="bar"
            :percentage="displayModuleCompletion"
            :precision="1"
            :show-bar-text="true"
            size="small"
            customText="Completed"
            ariaLabel="Overall Module Completion"
            progressTheme="darker"
          ></k-progress>
          <span class="comp-date" v-if="programmeModule.expected_completion_date">
            <b>Due by: </b>
            <b :class="dateIsPast(programmeModule.expected_completion_date) ? 'amber' : 'green'">{{ parseTimestamp(programmeModule.expected_completion_date, undefined, 'MMM d, yyyy') }}</b>
          </span>
        </div>
        <div v-if="hasAssets" class="module-toggle">
          <i class="fas" :class="toggleClass" aria-hidden="true"></i>
        </div>
      </div>
      <div v-if="!moduleDatesReady" class="updating-module-dates" role="status" aria-label="Loading">
        <i class="fas fa-spinner fa-spin" aria-hidden="true" />
      </div>
      <div v-else class="module-dates">
        <div>
          <!-- All module modification to date should be dictated by canManageModuleDates -->
          <span v-if="programmeModule.released"><b>Released on: </b>
            <k-editable-date-picker
              v-if="displayLearningPlanSection && canManageModuleDates"
              v-model="releaseDate"
              @change="updateModuleDates()"
            ></k-editable-date-picker>
            <span v-else>{{ parseTimestamp(releaseDate, undefined, 'MMM d, yyyy') }}</span>
          </span>
          <span v-else :class="releaseDate ? '' : 'amber'">
            Module not released. <b v-if="releaseDate" class="amber">Release due: </b>
            <k-editable-date-picker
              v-if="displayLearningPlanSection && canManageModuleDates"
              v-model="releaseDate"
              @change="updateModuleDates()"
            ></k-editable-date-picker>
            <span v-else>{{ parseTimestamp(releaseDate, undefined, 'MMM d, yyyy') }}</span>
          </span>
        </div>
        <div>
          <span v-if="expectedCompletionDate">
            <b>Due by: </b>
            <b :class="dateIsPast(expectedCompletionDate) ? 'amber' : 'green'">
              <k-editable-date-picker
                  v-if="displayLearningPlanSection && canManageModuleDates"
                  v-model="expectedCompletionDate"
                  @change="updateModuleDates()"
              ></k-editable-date-picker>
              <span v-else>{{ parseTimestamp(expectedCompletionDate, undefined, 'MMM d, yyyy') }}</span>
            </b>
          </span>
          <span v-else>
            <b>No completion date
              <k-editable-date-picker
                  v-if="displayLearningPlanSection && canManageModuleDates"
                  v-model="expectedCompletionDate"
                  @change="updateModuleDates()"
              ></k-editable-date-picker>
              <span v-else>{{ parseTimestamp(expectedCompletionDate, undefined, 'MMM d, yyyy') }}</span>
            </b>
          </span>
        </div>
        <button class="btn btn-tertiary reset-dates" @click="resetModuleDates" v-if="programmeModule.modified_module_dates && displayLearningPlanSection && canManageModuleDates">
          Reset dates
        </button>
      </div>
    </div>
    <div class="module-assets">
      <k-asset-counts
        v-if="hasAssets"
        :count-by-type="assetCounts"
      />
      <span v-else>No assets found for this module</span>
    </div>
    <div v-if="show" class="module-expanded-chapters">
      <k-loader v-if="chaptersLoading" :size="20"></k-loader>
      <template v-else-if="chaptersLoaded">
        <dashboard-chapter-panel v-for="chapter in sortedChapters" :key="chapter.name"
          :moduleId="programmeModule.id"
          :chapter="chapter"
          :user-id="userIdFromRoute"
          :programme-member-id="programmeMemberId"
          :is-learner-support="isLearnerSupport"
          @update-chapters="getChapters"
          @refresh-modules="$emit('refresh-modules')"
        ></dashboard-chapter-panel>
      </template>
    </div>
  </div>
</template>

<style>
#dashboard-module ul.k-asset-count {
  padding: 0;
  display: flex;
  gap: 5px;
  margin: 0;
  flex-wrap: wrap;
}

#dashboard-module ul.k-asset-count.border li {
  border-right: 1px solid var(--kate-type-primary);
  margin: 0;
  padding: 0;
  padding-right: 5px;
  font-size: 0.85em;
}

#dashboard-module ul.k-asset-count.border li:last-child {
  border-right: 0;
}

#dashboard-module ul.k-asset-count.border li span {
  color: var(--kate-type-primary);
}
</style>

<style scoped>
.module-card-container {
  box-shadow: var(--box-shadow-alt);
}

.panel-body > div {
  display: flex;
  gap: 15px;
  align-items: center;
  position: relative;
  justify-content: space-between;
  row-gap: 0;
}

.panel-body::before {
  display: none;
}

.module-name {
  flex: 1;
}

.module-dates {
  display: flex;
  flex-direction: column !important;
  align-items: flex-start !important;
}

.module-dates div {
  display: inline-block;
}

.module-progress {
  min-width: 250px;
  font-size: 0.85em;
}

.module-toggle {
  font-size: 1.2em;
}

.module-assets {
  padding: 0 15px 15px;
}

.module-expanded-chapters {
  padding: 0 15px 15px;
}

img {
  height: auto;
  max-width: 45px;
}

@media (max-width: 838px) {
  .panel-body {
    padding-right: 45px;
    flex-wrap: wrap;
  }

  .module-assets {
    flex: unset;
    width: 100%;
  }

  .module-toggle {
    top: 50%;
    right: 15px;
    bottom: 0;
    display: flex;
    position: absolute;
    align-items: center;
    transform: translateY(-50%);
  }

  .module-progress {
    min-width: 150px;
    width: 100%;
    font-size: 0.85em;
  }
}

/* Disabled modules */

.disabled .module-assets,
.disabled .module-progress,
.disabled .module-name h3,
.disabled .amber {
  opacity: 0.5;
}

</style>

<script>
import TimeMixins from '@mixins/time-mixins';
import ChaptersMixin from '@mixins/chapters-mixin';
import KProgress from '@base-components/k-progress.vue';
import KAssetCounts from '@base-components/k-asset-counts.vue';
import KLoader from '@base-components/k-loader.vue';
import DashboardChapterPanel from './dashboard-chapter-panel.vue';
import KEditableDatePicker from '../../../components/k-editable-date-picker.vue';

export default {
  mixins: [ChaptersMixin, TimeMixins],

  components: {
    KEditableDatePicker,
    KAssetCounts,
    KProgress,
    DashboardChapterPanel,
    KLoader,
  },

  props: {
    programmeId: Number,
    programmeModule: Object,
    programmeMemberId: Number,
    isLearnerSupport: Boolean,
  },

  data() {
    return {
      moduleDatesReady: true,
      show: false,
      chapters: [],
      expectedCompletionDate: null,
      releaseDate: null,
      chaptersLoaded: false,
      chaptersLoading: false,
      chaptersReady: true,
    };
  },

  mounted() {
    this.expectedCompletionDate = this.programmeModule.expected_completion_date;
    this.releaseDate = this.programmeModule.release_date;
  },

  computed: {
    userIdFromRoute() {
      return Number(this.$route.params.studentId);
    },

    displayModuleCompletion() {
      const n = Number(this.programmeModule?.module_completion_percent);
      return Number.isFinite(n) ? n : 0;
    },

    assetCounts() {
      return this.programmeModule?.asset_counts || {};
    },

    hasAssets() {
      return Object.keys(this.programmeModule?.asset_counts ?? {}).length > 0;
    },

    toggleClass() {
      return this.show ? 'fa-angle-up' : 'fa-angle-down';
    },

    displayLearningPlanSection() {
      return this.$featureFlags.isEnabled('kate-learning-plan');
    },

    canManageModuleDates() {
      return this.$permissions.hasPermission('manage_user_module_dates');
    },
  },

  methods: {
    onHeaderClick() {
      this.show = !this.show;
      if (this.show) {
        this.ensureChaptersLoaded();
      }
    },

    ensureChaptersLoaded() {
      if (this.chaptersLoaded || this.chaptersLoading) return;
      this.fetchChapters();
    },

    getChapters() {
      this.fetchChapters();
    },

    fetchChapters() {
      this.$logger.info('Getting chapters');
      this.chaptersLoading = true;
      this.chaptersReady = false;
      this.$http.get(`/api/curriculum/admin/modules/${this.programmeModule.id}/users/${this.userIdFromRoute}/chapters`)
        .then(res => {
          this.$logger.info('Got chapters');
          this.chapters = res.data.chapters || [];
          this.chaptersLoaded = true;
        }).catch(err => {
          this.$logger.warn('Error getting chapters', { id: this.programmeModule.id }, err);
        }).then(() => {
          this.chaptersLoading = false;
          this.chaptersReady = true;
        });
    },

    resetModuleDates() {
      this.moduleDatesReady = false;
      this.$http.delete(`/api/curriculum/admin/module/${this.programmeModule.id}/learner/${this.userIdFromRoute}/update-module-dates`).then(() => {
        this.$emit('refresh-modules');
      }).catch(err => {
        this.$logger.autowarn('Error resetting module dates', { id: this.programmeModule.id }, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.moduleDatesReady = true;
      });
    },

    updateModuleDates() {
      this.moduleDatesReady = false;
      this.$http.post(`/api/curriculum/admin/module/${this.programmeModule.id}/learner/${this.userIdFromRoute}/update-module-dates`, {
        expected_completion_date: this.expectedCompletionDate ? this.formatDate(this.expectedCompletionDate) : null,
        release_date: this.releaseDate ? this.formatDate(this.releaseDate) : null,
      }).then(() => {
        this.$emit('refresh-modules');
      }).catch(err => {
        this.$logger.autowarn('Error updating module dates', { id: this.programmeModule.id }, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.moduleDatesReady = true;
      });
    },
  },
};
</script>
